import {
  usePlanList,
  useTogglePlanMutation,
} from "@features/onboarding/utility-setup/hooks";
import {
  planFilters,
  PlanListItem,
} from "@features/onboarding/utility-setup/types";
import { GenericTable } from "@shared/components/GenericTable";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";
import { Switch } from "@shared/ui/switch";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Edit, Eye } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FilterPanel } from "./Filter";

const PlanList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const remoteUtilityId = useRemoteUtilityId();
  const itemsPerPage = PAGE_SIZE;
  const togglePlanMutation = useTogglePlanMutation();

  const getFiltersFromUrl = () => {
    const page = parseInt(searchParams.get("page") || "1");
    const query = searchParams.get("query") || "";
    const category = searchParams.get("category") || "";
    const utilityService = searchParams.get("utilityService") || "";
    return {
      page,
      limit: itemsPerPage,
      remote_utility_id: Number(remoteUtilityId),
      search_data: query, // Add search_data from URL query param
      category: category,
      utilityService: utilityService,
    };
  };

  const [filters, setFilters] = useState(getFiltersFromUrl);

  useEffect(() => {
    setFilters(getFiltersFromUrl());
  }, [searchParams, remoteUtilityId]);

  const {
    data,
    isLoading: loading,
    error,
  } = usePlanList(filters, remoteUtilityId);

  const onEdit = (row: PlanListItem) => {
    const stepMap: Record<number, string> = {
      1: "basic-details",
      2: "consumer-categories",
      3: "utility-services",
      4: "service-charges",
    };

    const navigationStepIndex = (row.currentStep ?? 0) + 1;

    const navigationStep = stepMap[navigationStepIndex];
    console.log("navigationStep", navigationStep);

    console.log("row", row, "→ navigating to:", navigationStep);
    navigate(`/plan-tarrif/plan/edit/${row.id}#${navigationStep}`, {
      state: { initialData: row },
    });
  };

  const onView = (row: PlanListItem) => {
    console.log("row", JSON.stringify(row, null, 2));
    navigate(`/plan-tarrif/plan/view/${row.id}/?tab=details`, {
      state: { initialData: row },
    });
  };

  const columns: TableColumn<PlanListItem>[] = [
    {
      key: "planName",
      header: "Plan Name",
      width: "w-64",
      render: (value) => (
        <span className="text-sm font-medium text-gray-900">{value}</span>
      ),
    },
    {
      key: "category",
      header: "Consumer Category",
      width: "w-48",
      render: (value) => <span className="text-sm text-gray-700">{value}</span>,
    },
    {
      key: "billingFrequencyDisplay",
      header: "Billing Frequency",
      width: "w-40",
      render: (value) => <span className="text-sm text-gray-700">{value}</span>,
    },
    {
      key: "validity",
      header: "Validity",
      width: "w-40",
      render: (value) => (
        <span className="text-sm text-gray-700">{value ?? "Ongoing"}</span>
      ),
    },
    {
      key: "utilityService",
      header: "Utility Service",
      width: "w-64",
      render: (services) => (
        <span className="text-sm text-gray-700">
          {Array.isArray(services) ? services.join(", ") : services}
        </span>
      ),
    },
    {
      key: "statusDisplay",
      header: "Status",
      width: "w-36",
      render: (value) => <span className="text-sm text-gray-700">{value}</span>,
    },
    {
      key: "actions",
      header: "Action",
      width: "w-36",
      type: "custom",
      render: (_: any, row: PlanListItem) => {
        const isLoading =
          togglePlanMutation.isPending &&
          togglePlanMutation.variables?.id === row.id;

        return (
          <div className="flex items-center gap-2">
            {/* View Plan */}
            <button
              onClick={() => onView(row)}
              className="text-blue-600 hover:text-blue-900"
              title="View Plan"
            >
              <Eye className="h-4 w-4" />
            </button>

            {/* Edit Plan */}
            <button
              onClick={() => onEdit(row)}
              className="text-blue-600 hover:text-blue-900"
              title="Edit Plan"
            >
              <Edit className="h-4 w-4" />
            </button>

            {/* Toggle Plan Status */}
            <div className="relative">
              <Switch
                checked={row.isActive}
                disabled={isLoading}
                onCheckedChange={() => handleStatusToggle(row)}
                title="Toggle Plan Status"
                className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-gray-300 relative inline-flex h-5 w-10 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out"
              >
                <span
                  className={`${
                    row.isActive ? "translate-x-5" : "translate-x-0"
                  } pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                />
              </Switch>
            </div>
          </div>
        );
      },
    },
  ];

  // Handle user status toggle
  const handleStatusToggle = (item: any) => {
    const payload = {
      id: item.id,
      is_active: !item.isActive,
    };

    togglePlanMutation.mutate(payload);
  };

  const handleApplyFilters = (newFilters: Partial<planFilters>) => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    params.delete("status");
    params.delete("department");
    params.delete("role");
    params.delete("page");

    if (newFilters.category) {
      params.set("category", newFilters.category);
    }
    if (newFilters.utilityService) {
      params.set("utilityService", newFilters.utilityService);
    }

    // Reset to page 1 when applying filters
    params.set("page", "1");
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);

    // Remove all filter params
    params.delete("status");
    params.delete("department");
    params.delete("role");
    params.delete("page");
    params.delete("query");
    params.delete("utilityService");
    params.delete("category");

    setSearchParams(params);
  };

  const handleSearch = (searchTerm: string, paramName: string) => {
    const params = new URLSearchParams(searchParams);
    if (searchTerm.trim()) {
      params.set(paramName, searchTerm);
      params.set("page", "1"); // Reset to first page when searching
    } else {
      params.delete(paramName);
    }
    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  const currentPage = parseInt(searchParams.get("page") || "1");

  return (
    <div>
      {/* <Button size="sm" className="gap-1 float-right" onClick={handleAdd}>
        <Plus className="h-4 w-4" /> Add Plan
      </Button> */}
      <div className="space-y-4">
        <GenericTable
          data={data?.result || []}
          columns={columns}
          searchPlaceholder="Search plans..."
          searchParamName="query"
          searchDebounceMs={300}
          onSearch={handleSearch}
          rowKey="id"
          pagination={{
            currentPage,
            totalPages: Math.ceil((data?.count || 0) / itemsPerPage),
            totalItems: data?.count || 0,
            itemsPerPage,
            startItem: (currentPage - 1) * itemsPerPage + 1,
            endItem: Math.min(currentPage * itemsPerPage, data?.count || 0),
          }}
          onPageChange={handlePageChange}
          filterPanel={
            <FilterPanel
              currentFilters={filters}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
            />
          }
          emptyMessage="No plans found"
        />
      </div>
    </div>
  );
};

export default PlanList;
